import axios from 'axios';
import { Form, Formik } from 'formik';
import React, { useCallback, useState } from 'react';
import { InferType, object, string } from 'yup';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/common/Button/Button';
import { login } from '../../api/authentication';
import { ErrorText, FormikInput, PasswordInput } from '../../components/common/Form';
import { useAuthContext } from '../../contexts/AuthContext';
import logoWithName from '../../images/TapTabLogoWhiteSmallWithName.png';
import { PAGE_FORGOT_PASSWORD } from '../../constants/UriConstants';
import { usePermissionsContext } from '../../contexts/PermissionsContext';
import tooManyAttemptsMessage from '../../utils/rateLimit';

const LoginSchema = object({
  email: string().max(50).default('').email('Must be a valid email').required('Email is required'),
  password: string().default('').required('Password is required')
});

function loginFailureMessage(error: unknown): string {
  const tooMany = tooManyAttemptsMessage(error);
  if (tooMany) return tooMany;
  if (axios.isAxiosError(error)) {
    if (error.response) {
      const { data, status } = error.response;
      if (typeof data === 'string' && data.trim()) {
        return data;
      }
      if (data && typeof data === 'object') {
        const body = data as { message?: unknown; error?: unknown };
        let message: string | undefined;
        if (typeof body.message === 'string') {
          message = body.message;
        } else if (typeof body.error === 'string') {
          message = body.error;
        }
        if (message?.trim()) {
          return message;
        }
      }
      if (status >= 500) {
        return 'Server error. Please try again later.';
      }
      return 'Invalid login credentials';
    }
    if (error.request) {
      return 'Cannot reach the API. Start TapManager-Backend (or fix REACT_APP_BASE_URL) and try again.';
    }
  }
  return 'Invalid login credentials';
}

const Login = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { setToken } = useAuthContext();
  const { setUserPermissions } = usePermissionsContext();
  const navigate = useNavigate();
  const [loginError, setLoginError] = useState(null);

  const handleLogin = ({ email, password }: InferType<typeof LoginSchema>) => {
    setIsLoading(true);
    setLoginError(null);
    login(email, password)
      .then((data) => {
        if (data?.token) {
          setToken(data.token);
          setUserPermissions({ hasImageUpload: data.hasImageUpload, hasPairings: data.hasPairings });
        } else {
          setLoginError(
            'The server response did not include a token. Ensure POST /login returns JSON with a `token` field (see LoginResponse).'
          );
        }
      })
      .catch((error) => {
        setLoginError(loginFailureMessage(error));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleForgotPasswordClicked = useCallback(() => {
    navigate(`/${PAGE_FORGOT_PASSWORD}`);
  }, [navigate]);

  const handleForgotPasswordKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      if (event.code === 'Enter' || event.code === 'Space') {
        navigate(`/${PAGE_FORGOT_PASSWORD}`);
      }
    },
    [navigate]
  );

  return (
    // url value is relative to index.css where tailwind is imported
    <div
      className={`h-screen w-screen bg-[#000]/[0.8] bg-[url('../images/LoginBackground.png')] bg-blend-darken backdrop-blur-sm bg-cover`}
    >
      <div className="h-screen w-screen backdrop-blur-md flex justify-center items-center">
        <div className="inline-block">
          <div className="flex items-center mb-10">
            <img src={logoWithName} className="h-[50px] pr-2" alt="logo" />
            <p className="text-[21px] text-white border-l-2 border-white pl-3">TapManager</p>
          </div>
          <ErrorText error={loginError ?? ''} className="mb-2" />
          <Formik initialValues={LoginSchema.cast({})} validationSchema={LoginSchema} onSubmit={handleLogin}>
            {({ errors, touched }) => (
              <Form className="grid">
                <FormikInput
                  name="email"
                  autoComplete="email"
                  placeholder="Email"
                  className="w-full mb-2"
                  onChange={() => setLoginError(null)}
                />
                <PasswordInput
                  name="password"
                  placeholder="Password"
                  className="w-full"
                  errors={errors}
                  touched={touched}
                  onChange={() => setLoginError(null)}
                />
                <Button
                  submit
                  overrideCSS
                  className="w-full h-[50px] mt-4 bg-[#979797] text-white text-[18px]"
                  isLoading={isLoading}
                >
                  Login
                </Button>
                <a
                  role="link"
                  className="w-full mt-4 text-white text-[12px] text-center cursor-pointer"
                  tabIndex={0}
                  onClick={handleForgotPasswordClicked}
                  onKeyPress={handleForgotPasswordKeyPress}
                >
                  Forgot Password?
                </a>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default Login;
